import React, { useState, useEffect, useCallback, useRef } from 'react';
import { User } from 'firebase/auth';
import { signOutUser, firebaseEnabled } from '../services/firebase';
// @ts-ignore - JSX component
import { useAuth } from '../AuthProvider';
import { AuthModal } from '../ui/components/AuthModal';
import { sdkStorageProvider } from '../services/storageProvider';
import { ParserTestModal } from '../ui/components/ParserTestModal';
import ViewerChrome from '../product/viewer/ViewerChrome';
import EditorPreview from '../product/editor/EditorPreview';
import { UserProfileModal } from '../ui/components/UserProfileModal';
// @ts-ignore - JSX component
import PresentationToolbar from '../components/PresentationToolbar.jsx';
// @ts-ignore - JSX component
import BottomNavigation from '../components/BottomNavigation.jsx';
// @ts-ignore - JSX component
import MarkdownEditorModal from '../components/MarkdownEditorModal.jsx';
import QuickOpenOverlay from '../ui/components/QuickOpenOverlay';
import { MobileDebugConsole } from '../ui/components/MobileDebugConsole';
import { CanvasLabelMesh } from '../engine/shapes/CanvasLabelMesh';
import { StorageApi, Configuration as SdkConfiguration } from '../sdk';
import { getAuthIdToken } from '../services/authToken';

import { EngineDemoApp } from '../engine/apps/EngineDemoApp';
import { MapPinsApp } from '../engine/apps/MapPinsApp';
import { KoralmApp } from '../koralm/KoralmApp';
import { TerrainApp } from '../terrain/TerrainApp';
// import { TechTestApp } from '../engine/apps/TechTestApp'; // Temporarily disabled - has TypeScript errors
import { SettingsProperty } from '../engine/properties';
import { SettingsPanel } from '../ui/components/SettingsPanel';
import DebugConsole from '../ui/components/DebugConsole';
import LogModal from '../ui/components/LogModal';
import { Logger, LogMessage } from '../engine/core/Logger';
import { POI } from '../engine/poi/POI';
import { Tooltip } from '../ui/components/Tooltip';
import { PanelManager } from '../ui/core/PanelManager';
import { TestingConfig } from '../engine/core/TestingConfig';
import { SettingsQuickFinder } from '../ui/components/SettingsQuickFinder';
import { Scene } from '../engine/core/Scene';
import { ExportManager, getExportParams } from '../engine/export/ExportManager';

/** The SPA fallback answers unknown paths with index.html; never treat that as markdown. */
function looksLikeHtml(content: string): boolean {
    const head = content.trimStart().slice(0, 64).toLowerCase();
    return head.startsWith('<!doctype') || head.startsWith('<html') || head.startsWith('<script');
}

// Define the state for our floating panel
interface FloatingPanelState {
    id: string; // Use POI's UUID
    title: string;
    // We no longer store properties in state. We'll get them fresh.
    x: number;
    y: number;
    // For QuickFinder panels - store the properties and search info
    quickFinderProperties?: SettingsProperty<any>[];
    quickFinderSearchTerm?: string;
}

interface TooltipState {
    visible: boolean;
    content: string;
    x: number;
    y: number;
}

const POI_PANEL_WIDTH = 340;
const POI_PANEL_HEIGHT = 450;

// This interface is no longer needed as we now use LogMessage from the logger.
/*
interface DebugMessage {
    id: number;
    text: string;
}
*/

export const EngineDemoUI: React.FC<{
    onShowLanding?: () => void;
    showLandingButton?: boolean;
    exportMode?: boolean;
    presentationId?: string | null;
    viewerMode?: boolean;
    authoringMode?: boolean;
    initialPresentation?: { id: string; title: string; content: string; filename?: string } | null;
    livePresentationContent?: string | null;
    onPresentationChange?: (content: string, title: string) => void;
}> = ({ onShowLanding, showLandingButton = false, exportMode = false, presentationId = null, viewerMode: viewerModeProp = false, authoringMode = false, initialPresentation = null, livePresentationContent = null, onPresentationChange }) => {
    const exportParams = exportMode ? getExportParams() : null;
    // Use TestingConfig for UI flags instead of local constants
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const cssContainerRef = useRef<HTMLDivElement>(null);
    const appRef = useRef<EngineDemoApp | null>(null); // Use ref to hold the app instance
    const panelManagerRef = useRef<PanelManager | null>(null);

    // State for UI components that depend on the app
    const [globalProperties, setGlobalProperties] = useState<SettingsProperty<any>[]>([]);
    const [debugMessages, setDebugMessages] = useState<LogMessage[]>([]);
    const [floatingPanels, setFloatingPanels] = useState<FloatingPanelState[]>([]);
    const [tooltip, setTooltip] = useState<TooltipState>({ visible: false, content: '', x: 0, y: 0 });
    const [isTooltipRendered, setIsTooltipRendered] = useState(false);
    const [showGlobalSettings, setShowGlobalSettings] = useState(false); // F1 toggle for global settings
    const [showDebugConsole, setShowDebugConsole] = useState(TestingConfig.showDebugConsole); // F2 toggle for debug console
    const [showLogModal, setShowLogModal] = useState(false); // Cmd+D/Ctrl+D toggle for advanced log modal
    const [showParserTest, setShowParserTest] = useState(false); // F8 toggle for parser test modal
    const [showQuickFinder, setShowQuickFinder] = useState(false); // F3 toggle for settings quick finder
    const [showQuickOpen, setShowQuickOpen] = useState(false);
    const [appReady, setAppReady] = useState(false);
    // Set when engine initialization rejects (no WebGL context, shader failure ...). Without
    // this the loading overlay stayed on screen forever and nobody learned why (Post #4963).
    const [initError, setInitError] = useState<string | null>(null);
    // A presentation named in the URL could not be loaded: shown instead of any fallback deck.
    const [loadError, setLoadError] = useState<string | null>(null);
    const [showLabelDiag, setShowLabelDiag] = useState(false);
    const [labelDiagText, setLabelDiagText] = useState('');

    // Authentication state
    const { user } = useAuth();
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showUserProfile, setShowUserProfile] = useState(false);
    
    // 🎪 Presentation Mode state
    const [isPresentationMode, setIsPresentationMode] = useState(false);
    
    // 📝 Markdown Editor state
    const [showMarkdownEditor, setShowMarkdownEditor] = useState(false);
    const [currentMarkdown, setCurrentMarkdown] = useState('');
    const [currentFilename, setCurrentFilename] = useState('test_field_pres copy.md');
    const [currentPresentation, setCurrentPresentation] = useState<{ id?: string; source: 'local' | 'sdk' | 'url'; fileName: string; title: string; storageId?: number } | null>(null);

    // Save state
    const [isSaving, setIsSaving] = useState(false);
    const [lastSavedContent, setLastSavedContent] = useState('');
    const [saveToast, setSaveToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
    const isDirty = currentMarkdown !== lastSavedContent && lastSavedContent !== '';

    const extractTitleFromMarkdownSafe = useCallback((markdown: string): string => {
        try {
            const m = markdown.match(/^#\s+(.+)$/m);
            if (m && m[1]) return m[1].trim();
            const line = markdown.split(/\r?\n/).find(l => l.trim().length > 0);
            return line ? line.replace(/^#+\s*/, '').trim().substring(0, 80) : 'Untitled';
        } catch {
            return 'Untitled';
        }
    }, []);

    useEffect(() => {
        if (!onPresentationChange || !hasLoadedInitialRef.current || !currentMarkdown) return;
        onPresentationChange(currentMarkdown, extractTitleFromMarkdownSafe(currentMarkdown));
    }, [currentMarkdown, extractTitleFromMarkdownSafe, onPresentationChange]);

    // Warn before closing tab with unsaved changes
    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (isDirty) { e.preventDefault(); e.returnValue = ''; }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    // Auto-dismiss toast after 3 seconds
    useEffect(() => {
        if (!saveToast) return;
        const timer = setTimeout(() => setSaveToast(null), 3000);
        return () => clearTimeout(timer);
    }, [saveToast]);

    const updateMarkdownTitle = useCallback((markdown: string, newTitle: string): string => {
        const lines = markdown.split(/\r?\n/);
        const idx = lines.findIndex(l => /^#\s+/.test(l));
        if (idx >= 0) {
            lines[idx] = `# ${newTitle}`;
            return lines.join('\n');
        }
        return `# ${newTitle}\n\n${markdown}`;
    }, []);

	// Persist last opened presentation (works for guests and users)
    // Guard to prevent double initial-loads (StrictMode/auth re-renders)
    const hasLoadedInitialRef = useRef(false);
    const lastAppliedLiveMarkdownRef = useRef<string | null>(null);
    const liveReloadGenerationRef = useRef(0);
	const getLastOpenKey = useCallback(() => {
		const uid = user?.uid || 'guest';
		return `3dp:last-opened:${uid}`;
	}, [user?.uid]);

    const getRecentListKey = useCallback(() => {
        const uid = user?.uid || 'guest';
        return `3dp:recent:${uid}`;
    }, [user?.uid]);

	const persistLastOpened = useCallback((content: string, filename: string) => {
		try {
			// Reject non-markdown content (e.g., failed fetches returning HTML)
			const trimmed = content.trim();
			if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html') || trimmed.startsWith('<script')) {
				console.warn('[persistLastOpened] Refusing to persist non-markdown content');
				return;
			}
			const ts = Date.now();
			const key = getLastOpenKey();
			localStorage.setItem(key, JSON.stringify({ filename, content, ts }));
			localStorage.setItem('3dp:last-opened-filename', filename);

            // Maintain a recent list (most-recent first, max 10)
            const recentKey = getRecentListKey();
            let list: Array<{ filename: string; content: string; ts: number }>; 
            try { list = JSON.parse(localStorage.getItem(recentKey) || '[]') || []; } catch { list = []; }
            // De-dup by exact filename+content; remove prior duplicates
            list = list.filter(item => !(item.filename === filename && item.content === content));
            list.unshift({ filename, content, ts });
            if (list.length > 10) list = list.slice(0, 10);
            localStorage.setItem(recentKey, JSON.stringify(list));
		} catch (e) {
			console.warn('Failed to persist last opened presentation', e);
		}
	}, [getLastOpenKey, getRecentListKey]);
    const collectLabelDiagnostics = useCallback(() => {
        try {
            const lines: string[] = [];
            const app: any = appRef.current;
            const scene: any = Scene.Instance;
            const cssRenderer = scene?.cssRenderer;
            const cssScene = scene?.cssScene;

            lines.push('# Label Diagnostics');
            lines.push('');
            // Global
            try {
                const as = (app as any)?.appSettings;
                if (as) {
                    lines.push('## AppSettings');
                    lines.push(`- labelRenderMode: ${String(as.labelRenderMode?.value)}`);
                    lines.push(`- labelScale: ${String(as.labelScale?.value)}`);
                    lines.push(`- labelMaxWidth: ${String(as.labelMaxWidth?.value)}`);
                    lines.push(`- labelBackgroundMode: ${String(as.labelBackgroundMode?.value)}`);
                    lines.push(`- labelParentMode: ${String(as.labelParentMode?.value)}`);
                    lines.push(`- fadeLabelOnActive: ${String(as.fadeLabelOnActive?.value)}`);
                    lines.push('');
                }
            } catch {}

            // CSS3D overlay
            lines.push('## CSS3D Overlay');
            lines.push(`- overlay present: ${!!cssRenderer}`);
            lines.push(`- overlay zIndex: ${cssRenderer?.domElement?.style?.zIndex ?? 'n/a'}`);
            lines.push(`- cssScene children: ${cssScene?.children?.length ?? 'n/a'}`);
            lines.push(`- DOM .html-label-mesh count: ${document.querySelectorAll('.html-label-mesh').length}`);
            lines.push('');

            // Active POI
            try {
                const poi = (app as any)?.getCurrentPOI?.();
                if (poi) {
                    lines.push('## Active POI');
                    lines.push(`- name: ${poi.name}`);
                    const pd = poi.poidata;
                    lines.push(`- showLabel: ${String(pd?.showLabel?.value)}`);
                    lines.push(`- poidata.labelRenderMode: ${String(pd?.labelRenderMode?.value)}`);
                    lines.push(`- html length: ${pd?.html?.value?.length ?? 0}`);
                    lines.push(`- markdown length: ${pd?.markdown?.value?.length ?? 0}`);
                    const label = poi.label || poi._label;
                    const renderType = label?.getRenderType?.() ?? 'n/a';
                    const visible = label?.visible ?? false;
                    lines.push(`- label type: ${String(renderType)}`);
                    lines.push(`- label visible: ${String(visible)}`);
                    try {
                        const el = (label as any)?._element as HTMLElement | undefined;
                        if (el) {
                            const rect = el.getBoundingClientRect();
                            lines.push(`- element: present (${Math.round(rect.width)}x${Math.round(rect.height)})`);
                        } else {
                            lines.push('- element: missing');
                        }
                    } catch { lines.push('- element: error'); }
                    lines.push('');
                }
            } catch {}

            // Sample first 5 POIs
            try {
                const pois: any[] = (app as any)?.pois || [];
                const count = Math.min(5, pois.length);
                lines.push('## Sample POIs');
                for (let i = 0; i < count; i++) {
                    const p = pois[i];
                    const label = p?.label || p?._label;
                    lines.push(`- ${i}: name="${p?.name}" showLabel=${String(p?.poidata?.showLabel?.value)} type=${String(label?.getRenderType?.() ?? 'n/a')} visible=${String(label?.visible ?? false)} htmlLen=${p?.poidata?.html?.value?.length ?? 0}`);
                }
                lines.push('');
            } catch {}

            setLabelDiagText(lines.join('\n'));
            setShowLabelDiag(true);
        } catch (e) {
            setLabelDiagText(`Error collecting diagnostics: ${String(e)}`);
            setShowLabelDiag(true);
        }
    }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            // Ctrl+Shift+L opens label diagnostics
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'L' || e.key === 'l')) {
                e.preventDefault();
                collectLabelDiagnostics();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [collectLabelDiagnostics]);
    
    // 📍 POI Navigation state
    const [currentPOI, setCurrentPOI] = useState(1);
    const [totalPOIs, setTotalPOIs] = useState(5); // Default for testing
    // Auto-play "film mode" — advances through POIs on a timer
    const [isAutoPlaying, setIsAutoPlaying] = useState(false);
    const AUTOPLAY_INTERVAL_MS = 12000;
    
    // 🎯 Layouter cycling state
    const [currentLayouterIndex, setCurrentLayouterIndex] = useState(0);
    const availableLayouters = ['grid', 'line', 'circle', 'spiral', 'hexagonal', 'spherical_hexagonal'];
    
    const lastClickPosition = useRef({ x: 0, y: 0 });

    useEffect(() => {
        if (!tooltip.visible) {
            const timer = setTimeout(() => {
                setIsTooltipRendered(false);
            }, 200); // This must match the CSS transition duration
            return () => clearTimeout(timer);
        }
    }, [tooltip.visible]);

    // 🔐 Authentication state logger (only log once when user changes)
    useEffect(() => {
        if (user) {
            console.log('✅ User signed in:', user.displayName || user.email);
            console.log('🌟 Full features available: Create, edit, and save presentations to cloud');
        } else {
            console.log('👋 Guest mode active - create and edit presentations locally');
            console.log('💡 Sign in anytime to save your work to the cloud');
        }
    }, [user]); // Only fire when user actually changes

    // 📍 POI State sync - Update POI count when app loads
    useEffect(() => {
        // For now using default testing values
        // TODO: Once the engine exposes POI info, sync real values:
        // if (appRef.current) {
        //     const poiCount = appRef.current.getPOICount();
        //     const currentPoi = appRef.current.getCurrentPOIIndex();
        //     setTotalPOIs(poiCount);
        //     setCurrentPOI(currentPoi + 1);
        // }
        //console.log(`📍 POI Navigation: ${currentPOI}/${totalPOIs} (test values)`);
    }, [currentPOI, totalPOIs]);

    // 🚀 PREFETCH: start the content network fetch immediately on first render, in parallel
    // with the WebGL engine init. Without this the fetch only starts after appReady=true,
    // serializing "engine init" → "network" into a waterfall. The load effect below awaits
    // this cached promise instead of re-fetching.
    const prefetchRef = useRef<Promise<{ kind: 'post', post: any } | { kind: 'none' }> | null>(null);
    if (prefetchRef.current === null) {
        const url = new URL(window.location.href);
        const postParam = url.searchParams.get('post');
        if (postParam && /^\d+$/.test(postParam.trim())) {
            const postId = parseInt(postParam.trim());
            const apiBase = (import.meta as any).env?.VITE_CONTENT_API_BASE || 'https://content-api.arkturian.com';
            console.info(`[UI:prefetch] Fetching post ${postId} in parallel with engine init`);
            prefetchRef.current = fetch(`${apiBase}/api/v1/posts/${postId}`)
                .then(r => r.ok ? r.json() : Promise.reject(new Error(`Content-API HTTP ${r.status}`)))
                .then(post => ({ kind: 'post' as const, post }))
                .catch(err => { console.warn('[UI:prefetch] failed, will retry in loader', err); return { kind: 'none' as const }; });
        } else {
            prefetchRef.current = Promise.resolve({ kind: 'none' as const });
        }
    }

    // 📄 Load initial presentation content
    // ⚠️ Skip entirely when using the code-first MapPinsApp
    useEffect(() => {
        const url = new URL(window.location.href);
        // Validated public documents always use the presenter, never a custom
        // app selected through a query parameter appended to their share URL.
        const appParam = viewerModeProp ? null : url.searchParams.get('app');
        const isMapApp = appParam === 'map';
        const isKoralmApp = appParam === 'koralm';
        const isTechTestApp = appParam === 'techtest';
        const isTerrainApp = appParam === 'terrain';
        if (isMapApp || isKoralmApp || isTechTestApp || isTerrainApp) {
            return; // Do not load markdown when using MapPinsApp/KoralmApp/TerrainApp
        }
        // Ensure engine is ready in production to avoid a race causing empty start
        if (!appReady) {
            console.info('[UI:init] Skipping - app not ready yet');
            return;
        }
        const loadInitialPresentation = async () => {
            Logger.Instance.Info(`loadInitialPresentation: user=${user?.uid || 'null'} appReady=${appReady} hasLoaded=${hasLoadedInitialRef.current} search=${window.location.search}`, 'Init');
            try {
                if (hasLoadedInitialRef.current) {
                    Logger.Instance.Info('Skipping duplicate initial load', 'Init');
                    return;
                }
                if (initialPresentation) {
                    const filename = initialPresentation.filename || `${initialPresentation.title || 'Untitled'}.md`;
                    const content = initialPresentation.content;
                    setCurrentFilename(filename);
                    setCurrentMarkdown(content);
                    setLastSavedContent(content);
                    setCurrentPresentation({
                        id: initialPresentation.id,
                        source: 'local',
                        fileName: filename,
                        title: initialPresentation.title || extractTitleFromMarkdownSafe(content),
                    });
                    if (appRef.current) await appRef.current.loadPresentationFromMarkdown(content, filename);
                    lastAppliedLiveMarkdownRef.current = content;
                    hasLoadedInitialRef.current = true;
                    Logger.Instance.Info(`Loaded product-shell draft: ${initialPresentation.id}`, 'Init');
                    return;
                }
                // 0) If storageId present: load from storage by id (takes precedence over last-opened)
                const storageIdParam = url.searchParams.get('storageid') || url.searchParams.get('storageId') || url.searchParams.get('storagedid');
                const storageId = storageIdParam ? parseInt(storageIdParam, 10) : NaN;
                Logger.Instance.Info(`storageIdParam=${storageIdParam} storageId=${storageId} appRef=${!!appRef.current}`, 'Init');
                if (!Number.isNaN(storageId) && storageId > 0) {
                    try {
                        Logger.Instance.Info(`Loading from SDK storage object ${storageId}...`, 'Init');
                        const { filename, content } = await sdkStorageProvider.loadPresentationByObjectId(storageId);
                        Logger.Instance.Info(`Got content: ${content.length} chars, filename=${filename}`, 'Init');
                        setCurrentMarkdown(content);
                        setCurrentFilename(filename);
                        setLastSavedContent(content);
                        setCurrentPresentation({ source: 'sdk', fileName: filename, title: extractTitleFromMarkdownSafe(content), storageId });
                        if (appRef.current) {
                            await appRef.current.loadPresentationFromMarkdown(content, filename);
                            Logger.Instance.Info(`Loaded into engine: ${filename}`, 'Init');
                        } else {
                            Logger.Instance.Warn('appRef.current is null - engine not ready', 'Init');
                        }
                        hasLoadedInitialRef.current = true;
                        return;
                    } catch (e: any) {
                        Logger.Instance.Error(`StorageID load failed: ${e?.message}`, 'Init');
                    }
                }

                // ?post=<id> → load from content-api (new doctype 3dpresentation pathway)
                const postParam = url.searchParams.get('post');
                if (postParam && /^\d+$/.test(postParam.trim())) {
                    const postId = parseInt(postParam.trim());
                    try {
                        // Use the prefetched result (started in parallel with engine init).
                        // Falls back to a direct fetch if the prefetch failed.
                        let post: any = null;
                        const prefetched = await prefetchRef.current;
                        if (prefetched && prefetched.kind === 'post') {
                            console.info(`[UI:init] Using prefetched post ${postId}`);
                            post = prefetched.post;
                        } else {
                            console.info(`[UI:init] Prefetch miss — fetching post ${postId} now`);
                            const apiBase = (import.meta as any).env?.VITE_CONTENT_API_BASE || 'https://content-api.arkturian.com';
                            const response = await fetch(`${apiBase}/api/v1/posts/${postId}`);
                            if (!response.ok) throw new Error(`Content-API HTTP ${response.status}`);
                            post = await response.json();
                        }
                        const content = post?.content;
                        if (typeof content !== 'string' || content.trim().length === 0) {
                            throw new Error('Post has no content');
                        }
                        const filename = `${post.slug || `post-${postId}`}.md`;
                        setCurrentFilename(filename);
                        setCurrentMarkdown(content);
                        setLastSavedContent(content);
                        setCurrentPresentation({ source: 'content' as any, fileName: filename, title: post.title || extractTitleFromMarkdownSafe(content), storageId: undefined, contentPostId: postId } as any);
                        if (appRef.current) {
                            await appRef.current.loadPresentationFromMarkdown(content, filename);
                            // Apply persisted camera state from metadata_json.camera (if present)
                            const cam = post?.metadata_json?.camera;
                            if (cam && typeof cam === 'object') {
                                const settings = (appRef.current as any).appSettings;
                                if (typeof cam.distance === 'number') settings?.cameraDistance?.setImmediate(cam.distance);
                                if (typeof cam.latitude === 'number') settings?.cameraLatitude?.setImmediate(cam.latitude);
                                if (typeof cam.longitude === 'number') settings?.cameraLongitude?.setImmediate(cam.longitude);
                                console.info(`[UI:init] Applied camera state from metadata_json:`, cam);
                            }
                        }
                        hasLoadedInitialRef.current = true;
                        console.info(`[UI:init] Loaded content post ${postId}: ${post.title}`);
                        return;
                    } catch (error) {
                        console.warn(`[UI:init] Failed to load post=${postId}`, error);
                    }
                }

                const presParam = presentationId || url.searchParams.get('pres') || url.searchParams.get('presentation');
                if (presParam && presParam.trim().length > 0) {
                    const normalized = presParam.trim();
                    try {
                        let content: string;
                        let filename: string;

                        // Numeric ID → load from Storage API as object ID
                        if (/^\d+$/.test(normalized)) {
                            console.info(`[UI:init] Loading presentation from storage ID: ${normalized}`);
                            const result = await sdkStorageProvider.loadPresentationByObjectId(parseInt(normalized));
                            content = result.content;
                            filename = result.filename;
                        } else {
                            // URL or local path
                            const resolvedUrl = normalized.startsWith('http')
                                ? normalized
                                : normalized.startsWith('/')
                                    ? normalized
                                    : `/presentations/${normalized}`;
                            console.info(`[UI:init] Loading presentation from URL: ${resolvedUrl}`);
                            const response = await fetch(resolvedUrl);
                            if (!response.ok) throw new Error(`HTTP ${response.status}`);
                            content = await response.text();
                            filename = resolvedUrl.split('/').pop() || 'presentation.md';
                        }

                        // The SPA answers unknown paths with index.html; never treat that as markdown.
                        if (looksLikeHtml(content)) {
                            throw new Error('Received HTML instead of markdown');
                        }

                        setCurrentFilename(filename);
                        setCurrentMarkdown(content);
                        setLastSavedContent(content);
                        setCurrentPresentation({ source: 'url', fileName: filename, title: extractTitleFromMarkdownSafe(content), storageId: /^\d+$/.test(normalized) ? parseInt(normalized) : undefined });
                        if (appRef.current) {
                            await appRef.current.loadPresentationFromMarkdown(content, filename);
                        }
                        hasLoadedInitialRef.current = true;
                        console.info(`[UI:init] Loaded presentation: ${filename}`);
                        return;
                    } catch (error) {
                        console.warn(`[UI:init] Failed to load presentation from pres param (${normalized})`, error);
                        // An explicitly requested presentation that fails must not be replaced by the
                        // last-opened or default deck (seen 2026-09-10: index.html rendered as stations).
                        setLoadError(`Angefragt: ${normalized}`);
                        hasLoadedInitialRef.current = true;
                        return;
                    }
                }

                const key = getLastOpenKey();
                const last = localStorage.getItem(key);
                if (last) {
                    const parsed = JSON.parse(last);
                    const filename = parsed.filename || 'Untitled.md';
                    const content = typeof parsed.content === 'string' ? parsed.content : '';
                    // Reject cached HTML content (from failed fetches)
                    if (looksLikeHtml(content)) {
                        console.warn('[Init] Removing corrupted localStorage cache (HTML content)');
                        localStorage.removeItem(key);
                    } else {
                        setCurrentFilename(filename);
                        setCurrentMarkdown(content);
                        setLastSavedContent(content);
                        setCurrentPresentation({ source: 'local', fileName: filename, title: extractTitleFromMarkdownSafe(content) });
                        if (appRef.current) {
                            await appRef.current.loadPresentationFromMarkdown(content, filename);
                            console.info(`Restored last opened (user key): ${filename}`);
                            hasLoadedInitialRef.current = true;
                        }
                        return;
                    }
                }

                // Fallback: try guest key if user key not present (e.g., saved before login)
                const guestKey = '3dp:last-opened:guest';
                const lastGuest = localStorage.getItem(guestKey);
                if (lastGuest) {
                    const parsed = JSON.parse(lastGuest);
                    const filename = parsed.filename || 'Untitled.md';
                    const content = typeof parsed.content === 'string' ? parsed.content : '';
                    const cg = content.trim();
                    if (cg.startsWith('<!DOCTYPE') || cg.startsWith('<html') || cg.startsWith('<script')) {
                        console.warn('[Init] Removing corrupted guest localStorage cache (HTML content)');
                        localStorage.removeItem(guestKey);
                    } else {
                        setCurrentFilename(filename);
                        setCurrentMarkdown(content);
                        setLastSavedContent(content);
                        setCurrentPresentation({ source: 'local', fileName: filename, title: extractTitleFromMarkdownSafe(content) });
                        if (appRef.current) {
                            await appRef.current.loadPresentationFromMarkdown(content, filename);
                            console.info(`Restored last opened (guest key): ${filename}`);
                            hasLoadedInitialRef.current = true;
                        }
                        return;
                    }
                }

                // Fallback: load from storage object id in URL (?storageid=123)
                // (Handled earlier above to avoid duplicate code)

                // Fallback: load default from public
                const fallbackFilename = 'test_field_pres copy.md';
                // ☁️ Cloud last-open pointer for authenticated users
                try {
                    if (user) {
                        const SDK_BASE_URL = (import.meta as any).env?.VITE_SDK_BASE_URL || 'https://api-storage.arkturian.com';
                        const context = 'userprefs';
                        const collectionId = '3dp:prefs';
                        const idToken = await getAuthIdToken();
                        const storageHeaders: Record<string, string> = {};
                        if (idToken) {
                            storageHeaders.Authorization = `Bearer ${idToken}`;
                        }
                        const storage = new StorageApi(new SdkConfiguration({
                            basePath: SDK_BASE_URL,
                            baseOptions: { headers: storageHeaders }
                        }));
                        // Find latest pointer for this user
                        const list = await storage.listObjectsStorageListGet(true, context, collectionId, undefined, user.uid);
                        const items = (list?.data?.items || []).filter((it: any) => /last-open\.json$/i.test(it.original_filename));
                        // Pick most recent by created_at
                        items.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
                        const pointer = items[0];
                        if (pointer && pointer.file_url) {
                            const res = await fetch(pointer.file_url, { headers: idToken ? { Authorization: `Bearer ${idToken}` } : {} });
                            if (res.ok) {
                                const json = await res.json().catch(() => null as any);
                                const fname = json?.filename as string | undefined;
                                if (fname) {
                                    // Attempt to fetch from server public folder as a fallback (or future: fetch from storage by key)
                                    const resp = await fetch(`/presentations/${fname}`);
                                    if (resp.ok) {
                                        const content = await resp.text();
                                        setCurrentMarkdown(content);
                                        setCurrentFilename(fname);
                                        setCurrentPresentation({ source: 'local', fileName: fname, title: extractTitleFromMarkdownSafe(content) });
                                        if (appRef.current) {
                                            await appRef.current.loadPresentationFromMarkdown(content, fname);
                                        }
                                        return;
                                    }
                                }
                            }
                        }
                    }
                } catch (e) {
                    console.warn('☁️ Could not restore from cloud pointer', e);
                }
                const response = await fetch(`/presentations/${fallbackFilename}`);
                const fallbackContent = response.ok ? await response.text() : '';
                if (response.ok && !looksLikeHtml(fallbackContent)) {
                    const content = fallbackContent;
                    setCurrentMarkdown(content);
                    setCurrentFilename(fallbackFilename);
                    setCurrentPresentation({ source: 'local', fileName: fallbackFilename, title: extractTitleFromMarkdownSafe(content) });
                    if (appRef.current) {
                        await appRef.current.loadPresentationFromMarkdown(content, fallbackFilename);
                    }
                    console.info(`📄 Loaded fallback presentation: ${fallbackFilename}`);
                    hasLoadedInitialRef.current = true;
                } else {
                    console.warn(`📄 Could not fetch fallback ${fallbackFilename}`);
                }
            } catch (error) {
                console.error('📄 Error loading initial presentation:', error);
            }
        };

        // Load when engine is ready. For URL params (storageid, pres) load immediately.
        // For user-specific content (last-opened), wait for auth state.
        const hasUrlParam = !!initialPresentation || !!presentationId || url.searchParams.has('storageid') || url.searchParams.has('storageId') || url.searchParams.has('pres') || url.searchParams.has('presentation') || url.searchParams.has('post');
        if (hasUrlParam || user !== null || !firebaseEnabled) {
            loadInitialPresentation();
        }
    }, [user, firebaseEnabled, getLastOpenKey, appReady, presentationId, initialPresentation]);

    // Product editor: keep one WebGL engine instance alive while the story model changes.
    // Reload is debounced so typing does not rebuild the scene for every key stroke.
    useEffect(() => {
        if (!authoringMode || !appReady || !hasLoadedInitialRef.current || livePresentationContent === null) return;
        if (livePresentationContent === lastAppliedLiveMarkdownRef.current) return;
        const generation = ++liveReloadGenerationRef.current;
        const timer = window.setTimeout(() => {
            const apply = async () => {
                if (!appRef.current || generation !== liveReloadGenerationRef.current) return;
                const filename = currentFilename || initialPresentation?.filename || 'Untitled.md';
                await appRef.current.loadPresentationFromMarkdown(livePresentationContent, filename);
                if (generation !== liveReloadGenerationRef.current) return;
                lastAppliedLiveMarkdownRef.current = livePresentationContent;
                setCurrentMarkdown(livePresentationContent);
            };
            void apply().catch((error) => console.error('[Editor] Live scene reload failed', error));
        }, 320);
        return () => window.clearTimeout(timer);
    }, [appReady, authoringMode, currentFilename, initialPresentation?.filename, livePresentationContent]);

    // Export Mode: auto-capture after presentation loads
    useEffect(() => {
        if (!exportMode || !exportParams || !appReady) return;
        // Wait for assets to load (GLTFs, textures), then export
        const timer = setTimeout(() => {
            // Navigate to requested POI if specified
            if (exportParams.poi > 0 && appRef.current) {
                for (let i = 0; i < exportParams.poi; i++) {
                    (appRef.current as any).nextPOI();
                }
                // Give camera time to animate to POI
                setTimeout(() => {
                    ExportManager.download({
                        width: exportParams.width,
                        height: exportParams.height,
                        format: exportParams.format,
                        filename: `title-${Date.now()}.${exportParams.format}`,
                    });
                }, 1000);
            } else if (exportParams.autoDownload) {
                ExportManager.download({
                    width: exportParams.width,
                    height: exportParams.height,
                    format: exportParams.format,
                    filename: `title-${Date.now()}.${exportParams.format}`,
                });
            }
        }, exportParams.delay);
        return () => clearTimeout(timer);
    }, [exportMode, exportParams, appReady]);

    // Expose ExportManager globally for console/devtools access
    useEffect(() => {
        (window as any).ExportManager = ExportManager;
    }, []);

    // Auth handlers
    const handleAuthSuccess = useCallback((user: User) => {
        // User state is managed by AuthProvider, just close modal
        setShowAuthModal(false);
    }, []);

    const handleSignOut = useCallback(() => {
        signOutUser().then(() => {
            // User state is managed by AuthProvider
            console.log('👋 User signed out successfully');
        }).catch((error: any) => {
            console.error('❌ Error signing out:', error);
        });
    }, []);

    // 🎪 Presentation Toolbar Handlers
    const handleTogglePresentation = useCallback(() => {
        setIsPresentationMode(!isPresentationMode);
        // Hide/show debug elements in presentation mode
        if (!isPresentationMode) {
            setShowGlobalSettings(false);
            setShowDebugConsole(false);
            setShowLogModal(false);
            setShowParserTest(false);
            setShowQuickFinder(false);
            setShowQuickOpen(false);
            setFloatingPanels([]);
        }
    }, [isPresentationMode]);

    // 🎯 Layouter cycling handler
    const handleCycleLayouter = useCallback(() => {
        if (!appRef.current) {
            console.warn('🎯 Cannot cycle layouter - app not initialized');
            return;
        }

        const nextIndex = (currentLayouterIndex + 1) % availableLayouters.length;
        const nextLayouter = availableLayouters[nextIndex];
        
        //console.log(`🎯 Cycling layouter: ${availableLayouters[currentLayouterIndex]} → ${nextLayouter}`);
        
        // Call setLayouter on the app instance
        appRef.current.setLayouter(nextLayouter);
        
        // Update the index for next cycle
        setCurrentLayouterIndex(nextIndex);
        
        // Show a brief notification
        //console.log(`✅ Layouter changed to: ${nextLayouter.toUpperCase()}`);
    }, [currentLayouterIndex, availableLayouters]);

    // Auto-play "film mode": advance to next POI when the current slide has finished.
    // For image slides we wait AUTOPLAY_INTERVAL_MS. For video slides we wait until the
    // video's 'ended' event (HLS loops, so we listen for the first natural full play —
    // duration-based fallback when ended doesn't fire because loop=true is set).
    useEffect(() => {
        if (!isAutoPlaying) return;
        let timerId: ReturnType<typeof setTimeout> | null = null;
        let endedHandler: (() => void) | null = null;
        let watchedVideo: HTMLVideoElement | null = null;
        let cancelled = false;

        const advance = () => {
            if (cancelled || !appRef.current) return;
            appRef.current.nextPOI();
            setCurrentPOI(appRef.current.getCurrentPOIIndex());
        };

        const scheduleForCurrentSlide = () => {
            if (cancelled || !appRef.current) return;
            const vidInfo = (appRef.current as any).getCurrentPOIVideoInfo?.();
            if (vidInfo && vidInfo.videoEl) {
                watchedVideo = vidInfo.videoEl;
                // Wait until video reaches end of one full play, then advance.
                // Video has loop=true so we can't rely on 'ended' — use timeupdate
                // and detect when currentTime wraps back to ~0 from near-end.
                let nearEnd = false;
                endedHandler = () => {
                    const v = watchedVideo;
                    if (!v) return;
                    const dur = v.duration || vidInfo.durationSec || 0;
                    if (!dur || !isFinite(dur)) return;
                    if (v.currentTime > dur - 0.3) nearEnd = true;
                    else if (nearEnd && v.currentTime < 0.3) {
                        // Just wrapped: video played through once → advance.
                        if (endedHandler && watchedVideo) watchedVideo.removeEventListener('timeupdate', endedHandler);
                        endedHandler = null;
                        watchedVideo = null;
                        advance();
                    }
                };
                (watchedVideo as HTMLVideoElement).addEventListener('timeupdate', endedHandler);
                // Safety: if video never plays (e.g. autoplay blocked), advance after duration + slack
                const safetyMs = Math.max(AUTOPLAY_INTERVAL_MS, (vidInfo.durationSec || 12) * 1000 + 3000);
                timerId = setTimeout(advance, safetyMs);
            } else {
                // Image slide: fixed interval
                timerId = setTimeout(advance, AUTOPLAY_INTERVAL_MS);
            }
        };

        // After each advance the active POI changes — re-schedule. We tie a chain through
        // currentPOI: when currentPOI updates while autoplay is on, this effect's deps
        // include currentPOI so it re-runs and the cleanup+setup cycle handles the next slide.
        scheduleForCurrentSlide();

        return () => {
            cancelled = true;
            if (timerId) clearTimeout(timerId);
            if (endedHandler && watchedVideo) watchedVideo.removeEventListener('timeupdate', endedHandler);
        };
    }, [isAutoPlaying, currentPOI]);

    const handleToggleAutoPlay = useCallback(() => {
        setIsAutoPlaying(p => !p);
    }, []);

    const handleToolbarPreviousPOI = useCallback(() => {
        setIsAutoPlaying(false); // manual interaction pauses auto-play
        if (appRef.current) {
            appRef.current.previousPOI();
            setTimeout(() => {
                if (appRef.current) {
                    const newPOIIndex = appRef.current.getCurrentPOIIndex();
                    setCurrentPOI(newPOIIndex);
                }
            }, 50);
        } else {
            console.warn('🔍 Button Previous POI clicked but appRef.current is null');
        }
    }, [totalPOIs]);

    const handleToolbarNextPOI = useCallback(() => {
        setIsAutoPlaying(false); // manual interaction pauses auto-play
        if (appRef.current) {
            appRef.current.nextPOI();
            setTimeout(() => {
                if (appRef.current) {
                    const newPOIIndex = appRef.current.getCurrentPOIIndex();
                    setCurrentPOI(newPOIIndex);
                }
            }, 50);
        } else {
            console.warn('🔍 BUTTON DEBUG: appRef.current is null!');
        }
    }, [totalPOIs]);

    const handleToolbarLoadPresentation = useCallback(() => {
        setShowQuickOpen(true);
    }, []);

    // Global shortcuts: Cmd/Ctrl+O and F9 open Quick Open overlay
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            const active = document.activeElement as HTMLElement | null;
            const isFormFocus = active && ['INPUT', 'TEXTAREA'].includes(active.tagName);
            // Cmd/Ctrl+O
            if ((e.metaKey || e.ctrlKey) && (e.key === 'o' || e.key === 'O')) {
                e.preventDefault();
                if (!isPresentationMode && !isFormFocus) setShowQuickOpen(true);
                return;
            }
            // F9
            if (e.key === 'F9') {
                e.preventDefault();
                if (!isPresentationMode) setShowQuickOpen(true);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isPresentationMode]);

    const handleToolbarEditPresentation = useCallback(() => {
        // Get current markdown from the app if available
        // TODO: Implement getCurrentMarkdown() in EngineDemoApp
        const currentContent = currentMarkdown;
        setCurrentMarkdown(currentContent);
        setShowMarkdownEditor(true);
    }, [currentMarkdown]);

    const handleToolbarShowSettings = useCallback(() => {
        setShowGlobalSettings(true);
    }, []);

    const handleToolbarShowAuth = useCallback(() => {
        if (user) {
            // User is logged in - show profile modal
            setShowUserProfile(true);
        } else {
            // User is not logged in - show auth modal
            setShowAuthModal(true);
        }
    }, [user]);

    /**
     * Save current camera position (distance/latitude/longitude) to the post's metadata_json.
     * This only touches the JSON metadata — content stays untouched, no new content revision.
     * Read-mutate-write because metadata_json PATCH replaces the whole object (no sub-merge).
     */
    const handleSaveCameraPosition = useCallback(async () => {
        const postId = (currentPresentation as any)?.contentPostId;
        if (!postId) {
            setSaveToast({ message: 'Camera save only works for content posts (?post=)', type: 'error' });
            return;
        }
        setSaveToast({
            message: 'Cloud writing is temporarily unavailable while secure user authentication is being enabled.',
            type: 'error'
        });
    }, [currentPresentation]);

    /**
     * Export current runtime settings (camera, lights, layouter params, per-POI tweaks)
     * back into markdown and save. Persists user adjustments to the stored presentation.
     */
    const handleSaveCurrentSettings = useCallback(async () => {
        if (!appRef.current) return;
        try {
            const exported = await (appRef.current as any).exportToMarkdown();
            if (exported && typeof exported === 'string') {
                await handleMarkdownSaveRef.current?.(exported);
            }
        } catch (err) {
            console.error('Failed to export current settings:', err);
            setSaveToast({ message: `Settings export failed`, type: 'error' });
        }
    }, []);

    const handleMarkdownSaveRef = useRef<((content: string) => Promise<void>) | null>(null);

    const handleMarkdownSave = useCallback(async (content: string) => {
        setIsSaving(true);
        setCurrentMarkdown(content);
        try { persistLastOpened(content, currentFilename); } catch {}

        // Apply changes to engine
        if (appRef.current) {
            try {
                await appRef.current.loadPresentationFromMarkdown(content, currentFilename);
                setGlobalProperties(appRef.current!.getGlobalSettingsForUI());
            } catch (error: any) {
                Logger.Instance.Error(`Failed to update presentation: ${currentFilename}`, 'UI');
                console.error('Presentation update error:', error);
            }
        }

        // Determine save target: content post takes precedence over storage object
        const contentPostId = (currentPresentation as any)?.contentPostId;

        if (contentPostId) {
            // Never ship a privileged Content API credential in the browser.
            // The edit is already applied and persisted locally above.
            setLastSavedContent(content);
            setSaveToast({
                message: 'Saved locally; cloud writing is disabled until secure user authentication is available.',
                type: 'info'
            });
        } else if (user) {
            try {
                // Determine storageId: from currentPresentation or URL param
                let storageId = currentPresentation?.storageId;
                if (!storageId) {
                    const url = new URL(window.location.href);
                    const param = url.searchParams.get('storageid') || url.searchParams.get('storageId');
                    if (param) storageId = parseInt(param, 10) || undefined;
                }

                if (storageId) {
                    await sdkStorageProvider.savePresentationToObjectId(storageId, content, currentFilename);
                } else {
                    const saved = await sdkStorageProvider.saveOrUpdatePresentation(
                        content, extractTitleFromMarkdownSafe(content), false, user.email || undefined
                    );
                    setCurrentPresentation(prev => prev
                        ? { ...prev, storageId: saved.id, source: 'sdk' as const }
                        : { source: 'sdk' as const, fileName: currentFilename, title: extractTitleFromMarkdownSafe(content), storageId: saved.id }
                    );
                }
                setLastSavedContent(content);
                setSaveToast({ message: 'Saved to cloud', type: 'success' });
            } catch (e: any) {
                console.warn('SDK storage save failed:', e);
                setSaveToast({ message: `Save failed: ${e?.message || 'Unknown error'}`, type: 'error' });
            }
        } else {
            setLastSavedContent(content);
            setSaveToast({ message: 'Saved locally (sign in for cloud)', type: 'info' });
        }

        setShowMarkdownEditor(false);
        setIsSaving(false);
    }, [currentFilename, user, currentPresentation]);

    // Keep ref in sync so handleSaveCurrentSettings can call the latest save fn
    useEffect(() => { handleMarkdownSaveRef.current = handleMarkdownSave; }, [handleMarkdownSave]);

    // F1 keyboard shortcut to toggle global settings, F2 for debug console, Cmd+D/Ctrl+D for advanced log modal, F3 for quick finder, F8 for parser test
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'F1') {
                event.preventDefault(); // Prevent browser's default F1 behavior
                setShowGlobalSettings(prev => {
                    const newState = !prev;
                    ////console.log(`🎯 F1 pressed: Global Settings ${newState ? 'SHOWN' : 'HIDDEN'}`);
                    return newState;
                });
            } else if (event.key === 'F2') {
                event.preventDefault(); // Prevent browser's default F2 behavior
                setShowDebugConsole(prev => {
                    const newState = !prev;
                    ////console.log(`🔍 F2 pressed: Debug Console ${newState ? 'SHOWN' : 'HIDDEN'}`);
                    return newState;
                });
            } else if (event.key === 'F3') {
                event.preventDefault(); // Prevent browser's default F3 behavior
                setShowQuickFinder(prev => {
                    const newState = !prev;
                    //Logger.Instance.Info(`🔍 F3 pressed: Settings Quick Finder ${newState ? 'SHOWN' : 'HIDDEN'}`, 'UI');
                    return newState;
                });
            } else if (event.key === 'd' && (event.ctrlKey || event.metaKey)) {
                event.preventDefault(); // Prevent browser's default Cmd+D behavior
                setShowLogModal(prev => {
                    const newState = !prev;
                    //Logger.Instance.Info(`🔬 Cmd+D/Ctrl+D pressed: Advanced Log Modal ${newState ? 'SHOWN' : 'HIDDEN'}`, 'UI');
                    return newState;
                });
            } else if (event.key === 'F8') {
                event.preventDefault(); // Prevent browser's default F8 behavior
                setShowParserTest(prev => {
                    const newState = !prev;
                    //Logger.Instance.Info(`🧪 F8 pressed: Parser Test Modal ${newState ? 'SHOWN' : 'HIDDEN'}`, 'UI');
                    return newState;
                });
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        //console.log('🌌 Press F1 to toggle Global Settings panel, F2 to toggle Debug Console, F3 for Settings Quick Finder, Cmd+D/Ctrl+D for Advanced Log Modal, F8 to toggle Parser Test, F9 for Presentation Loader');
        
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        // Prevent re-initialization on re-renders
        if (appRef.current) {
            return;
        }

        // --- Initialize Managers ---
        const panelManager = new PanelManager(window.innerWidth, window.innerHeight);
        panelManagerRef.current = panelManager;
        
        // Register the static global settings panel so others can avoid it
        panelManager.addPanel('global-settings', {
            x: 20,
            y: 20,
            width: 340,
            height: 550,
        });

        const url = new URL(window.location.href);
        const appParam = url.searchParams.get('app');
        const useMapApp = appParam === 'map';
        const useKoralmApp = appParam === 'koralm';
        const useTerrainApp = appParam === 'terrain';
        // const useTechTestApp = appParam === 'techtest'; // Temporarily disabled

        const koralmLimitParam = url.searchParams.get('koralmLimit');
        const koralmCategories = url.searchParams.getAll('koralmCategory');
        const koralmLimit = koralmLimitParam ? Number.parseInt(koralmLimitParam, 10) : undefined;

        const engineApp = useMapApp
            ? new MapPinsApp(canvasRef.current!, cssContainerRef.current!)
            : useTerrainApp
            ? new TerrainApp(canvasRef.current!, cssContainerRef.current!)
            : useKoralmApp
                ? new KoralmApp(canvasRef.current!, cssContainerRef.current!, {
                    contentLimit: Number.isFinite(koralmLimit) ? koralmLimit : undefined,
                    categories: koralmCategories.length > 0 ? koralmCategories : undefined,
                })
                : new EngineDemoApp(canvasRef.current!, cssContainerRef.current!);
        if (engineApp instanceof EngineDemoApp) {
            engineApp.suppressDemoLoad = true;
        }
        // Suppress html2canvas usage to avoid document.write() warnings in production
        //try { CanvasLabelMesh.setGlobalUseHtml2Canvas(false); } catch {}
        // Also force label render mode away from Canvas/HTML to Screen HTML Static (no html2canvas)
        // try {
        //     const appSettings: any = (engineApp as any).appSettings;
        //     if (appSettings && appSettings.labelRenderMode) {
        //         appSettings.labelRenderMode.value = 'screen-html-static';
        //     }
        // } catch {}
        appRef.current = engineApp; // Store instance in ref
        try { (window as any).engineApp = engineApp; } catch {}

        // Cleanup callbacks registered asynchronously (inside .then) are collected here so
        // the useEffect's own cleanup can run them. Returning a function from inside .then()
        // does NOT register a React cleanup — that was a silent memory leak.
        const deferredCleanups: Array<() => void> = [];

        engineApp.initialize().then(() => {
            setAppReady(true);
            // Set state that depends on the initialized app
            setGlobalProperties(engineApp.getGlobalSettingsForUI());
            engineApp.start();

            // Only load default presentation if NOT using a specialized app
            // if (!useMapApp && !useKoralmApp && !useTechTestApp && engineApp instanceof EngineDemoApp) {
            //     loadInitialPresentation(engineApp);
            // }

            // 🎯 FIX: Initialize POI state from app
            const poiCount = engineApp.getPOICount();
            const currentPOIIndex = engineApp.getCurrentPOIIndex();
            setTotalPOIs(poiCount);
            setCurrentPOI(currentPOIIndex);
            //console.log(`📍 Initial POI State: ${currentPOIIndex}/${poiCount}`);

            // Setup logger
            const unsubscribeLogger = Logger.Instance.subscribe((message: LogMessage) => {
                setDebugMessages(prev => {
                    const newMessages = [...prev];
                    // If we have 25 or more messages, remove the oldest one first.
                    if (newMessages.length >= 25) {
                        newMessages.shift(); // Removes the first element
                    }
                    newMessages.push(message); // Add the new message
                    return newMessages;
                });
            });
            
            // Test logger connection
            //Logger.Instance.Info("🔧 Debug console enabled - logger is working!");
            //console.log("🔧 Debug console enabled - logger is working!");
            
            // Setup UI event listeners
            const handleShowProperties = (poi: POI) => {
                setFloatingPanels(prevPanels => {
                    const existingPanelIndex = prevPanels.findIndex(p => p.id === poi.id);

                    // If panel already exists, move it to the end of the array to bring it to the front.
                    if (existingPanelIndex !== -1) {
                        const panelToMove = prevPanels[existingPanelIndex];
                        const otherPanels = prevPanels.filter((_, index) => index !== existingPanelIndex);
                        return [...otherPanels, panelToMove];
                    }

                    // If panel does not exist, create a new one.
                    if (panelManagerRef.current) {
                        const panelWidth = window.innerWidth * 0.25; // 25% of screen width
                        const panelHeight = window.innerHeight - 200; // Full height minus 2 * 100px margin (top + bottom)
                        const newPanelInfo = panelManagerRef.current.addPanel(poi.id, {
                            x: 100, // 100px margin from left
                            y: 100, // 100px margin from top
                            width: panelWidth,
                            height: panelHeight,
                        });

                        const newPanel: FloatingPanelState = {
                            id: poi.id,
                            title: poi.name,
                            x: newPanelInfo.x,
                            y: newPanelInfo.y,
                        };
                        return [...prevPanels, newPanel];
                    }

                    return prevPanels; // Should not happen
                });
            };
            engineApp.eventManager.on('ui:show-properties-for-poi', handleShowProperties);

            // Keep currentPOI/totalPOIs in sync on engine changes (keyboard, programmatic)
            const handlePOIChanged = (data: { index: number, total: number }) => {
                setTotalPOIs(data.total);
                setCurrentPOI(data.index);
            };
            engineApp.eventManager.on('ui:poi-changed', handlePOIChanged);

            // --- Tooltip Event Handlers ---
            const handleShowTooltip = (data: { content: string, x: number, y: number }) => {
                setTooltip({ visible: true, ...data });
                setIsTooltipRendered(true);
            };
            const handleHideTooltip = () => {
                setTooltip(prev => ({ ...prev, visible: false }));
            };
            engineApp.eventManager.on('ui:show-tooltip', handleShowTooltip);
            engineApp.eventManager.on('ui:hide-tooltip', handleHideTooltip);

            // --- Window Resize Handler ---
            const handleResize = () => {
                panelManagerRef.current?.updateScreenSize(window.innerWidth, window.innerHeight);
            };
            window.addEventListener('resize', handleResize);

            // Register all async-created listeners for the effect's real cleanup below.
            deferredCleanups.push(() => {
                unsubscribeLogger();
                engineApp.eventManager.off('ui:show-properties-for-poi', handleShowProperties);
                engineApp.eventManager.off('ui:poi-changed', handlePOIChanged);
                engineApp.eventManager.off('ui:show-tooltip', handleShowTooltip);
                engineApp.eventManager.off('ui:hide-tooltip', handleHideTooltip);
                window.removeEventListener('resize', handleResize);
            });
        }).catch((error: unknown) => {
            const message = error instanceof Error ? error.message : String(error);
            Logger.Instance.Error(`Engine initialization failed: ${message}`, 'Init');
            setInitError(message);
            // Take the engine's own loading overlay down so the error is readable.
            try { (engineApp as any).loadingManager?.complete?.(); } catch {}
        });

        const handleMouseUp = (event: MouseEvent) => {
            lastClickPosition.current = { x: event.clientX, y: event.clientY };
        };
        window.addEventListener('mouseup', handleMouseUp);

        // REAL effect cleanup — runs listeners registered in .then() plus engine disposal.
        return () => {
            window.removeEventListener('mouseup', handleMouseUp);
            deferredCleanups.forEach(fn => { try { fn(); } catch {} });
            try { engineApp.dispose(); } catch {}
            if (appRef.current === engineApp) appRef.current = null;
        };
    }, []);
    
    const handlePropertyChanged = (property: SettingsProperty<any>, value: any) => {
        // The original implementation was correct. We have the property object.
        property.value = value;
    };

    const handleClosePanel = (panelId: string) => {
        setFloatingPanels(panels => panels.filter(p => p.id !== panelId));
        panelManagerRef.current?.removePanel(panelId);
    };

    // Handle search results selection from QuickFinder
    const handleQuickFinderSearchResults = (results: any[], searchTerm: string) => {
        // Extract properties from results
        const properties = results.map(result => result.property);
        
        // Create a dialog with all found properties
        const propertyPanel: FloatingPanelState = {
            id: `quickfinder-${searchTerm}-${Date.now()}`,
            title: `Search: "${searchTerm}" (${results.length} results)`,
            x: window.innerWidth / 2 - 250,
            y: window.innerHeight / 2 - 200,
            quickFinderProperties: properties,
            quickFinderSearchTerm: searchTerm,
        };

        setFloatingPanels(prev => [...prev, propertyPanel]);
        
        // Add panel to manager
        if (panelManagerRef.current) {
            panelManagerRef.current.addPanel(propertyPanel.id, {
                x: propertyPanel.x,
                y: propertyPanel.y,
                width: 500,
                height: Math.min(400, 100 + results.length * 60), // Dynamic height based on result count
            });
        }

        //Logger.Instance.Info(`🔍 QuickFinder: Showing ${results.length} results for "${searchTerm}"`, 'UI');
    };

    // Handle presentation loading
    const handleLoadPresentation = (content: string, filename: string) => {
        // Warn about unsaved changes
        if (isDirty && !confirm('You have unsaved changes. Load anyway?')) return;

        if (appRef.current) {
            setCurrentMarkdown(content);
            setCurrentFilename(filename);
            setLastSavedContent(content);
            setCurrentPresentation({ source: 'local', fileName: filename, title: extractTitleFromMarkdownSafe(content) });
            // Persist as last opened
            try {
                persistLastOpened(content, filename);
                console.info(`💾 Persisted last opened: ${filename}`);
            } catch {}
            
            // Save last presentation for logged-in users
            if (user) {
                const presentationData = {
                    filename: filename,
                    content: content,
                    timestamp: Date.now()
                };
                localStorage.setItem(`3d-presenter-last-presentation-${user.uid}`, JSON.stringify(presentationData));
                //console.log(`💾 Saved last presentation for user: ${filename}`);

                // ☁️ Persist cloud last-open pointer for cross-device restore
                // Pointer upload disabled; rely on uploaded presentation list
            }
            
            appRef.current.loadPresentationFromMarkdown(content, filename).then(() => {
                console.info(`📄 Loaded presentation into engine: ${filename}`);
                // Refresh global properties after loading new presentation
                setGlobalProperties(appRef.current!.getGlobalSettingsForUI());
                
                // 🎯 FIX: Update POI count and current POI for navigation
                const poiCount = appRef.current!.getPOICount();
                const currentPOIIndex = appRef.current!.getCurrentPOIIndex();
                setTotalPOIs(poiCount);
                setCurrentPOI(currentPOIIndex);
                //console.log(`📍 POI State updated: ${currentPOIIndex}/${poiCount}`);
            }).catch((error: any) => {
                Logger.Instance.Error(`📄 Failed to load presentation: ${filename}`, 'UI');
                console.error('Presentation loading error:', error);
            });
        }
    };

    // Early return if UI is disabled for testing
    if (TestingConfig.disableUI) {
        return <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />;
    }

    // Embed mode (?embed=1): the presenter runs as a pure renderer inside a host iframe
    // (Bridge Contract v0.2, Post 4263). All presenter chrome is hidden — the host owns
    // every visible UI element (drop-up, sheets, navigation).
    const embedMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('embed') === '1';

    // In export mode, render only the canvas — no UI at all
    // Zuschauer-Modus (?view=1): geteilte Links zeigen die Story, aber keine
    // Bearbeitungswerkzeuge. Navigation bleibt sichtbar — anders als im Embed.
    const viewerMode = viewerModeProp || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('view') === '1');
    const hideAllUI = exportMode || isPresentationMode || embedMode || viewerMode || authoringMode;

    return (
        <div style={{ position: 'relative', width: authoringMode ? '100%' : '100vw', height: authoringMode ? '100%' : '100vh' }}>
            {/* Export Mode: minimal floating button to manually trigger download */}
            {initError && (
                <div data-testid="engine-init-error" role="alert" style={{ position: 'absolute', inset: 0, zIndex: 5000, display: 'grid', placeItems: 'center', background: 'rgba(20,10,12,0.94)', color: '#FBF7F4', fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif", padding: 24 }}>
                    <div style={{ maxWidth: 460, textAlign: 'center' }}>
                        <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 10 }}>Der 3D-Raum konnte nicht gestartet werden.</div>
                        <div style={{ fontSize: 15, lineHeight: 1.55, opacity: 0.85, marginBottom: 18 }}>
                            Dein Browser stellt keine 3D-Grafik (WebGL) bereit oder blockiert sie. Versuche einen aktuellen Chrome, Safari oder Firefox mit aktivierter Hardwarebeschleunigung.
                        </div>
                        <div style={{ fontSize: 12.5, opacity: 0.6, marginBottom: 18, wordBreak: 'break-word' }}>{initError}</div>
                        <button onClick={() => window.location.reload()} style={{ background: '#C1121C', color: '#FBF7F4', border: 0, borderRadius: 999, padding: '11px 22px', fontSize: 14.5, fontWeight: 700, cursor: 'pointer' }}>Neu laden</button>
                    </div>
                </div>
            )}
            {loadError && !initError && (
                <div data-testid="presentation-load-error" role="alert" style={{ position: 'absolute', inset: 0, zIndex: 5000, display: 'grid', placeItems: 'center', background: 'rgba(20,10,12,0.94)', color: '#FBF7F4', fontFamily: 'system-ui, -apple-system, sans-serif', padding: 24 }}>
                    <div style={{ maxWidth: 460, textAlign: 'center' }}>
                        <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 10 }}>Präsentation nicht gefunden.</div>
                        <div style={{ fontSize: 15, lineHeight: 1.55, opacity: 0.85, marginBottom: 18 }}>Dieser Link ist nicht verfügbar oder wurde nicht veröffentlicht.</div>
                        <div style={{ fontSize: 12.5, opacity: 0.6, marginBottom: 18, wordBreak: 'break-word' }}>{loadError}</div>
                        <a href="/" style={{ color: '#FBF7F4', fontSize: 14.5, fontWeight: 600 }}>Zur Startseite</a>
                    </div>
                </div>
            )}
            {exportMode && appReady && (
                <button
                    onClick={() => ExportManager.download({
                        width: exportParams?.width,
                        height: exportParams?.height,
                        format: exportParams?.format,
                        filename: `title-${Date.now()}.${exportParams?.format || 'png'}`,
                    })}
                    style={{
                        position: 'fixed', bottom: 20, right: 20, zIndex: 9999,
                        padding: '12px 24px', borderRadius: 12, border: 'none',
                        background: 'rgba(33,150,243,0.9)', color: 'white',
                        fontSize: 16, fontWeight: 600, cursor: 'pointer',
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    Download PNG
                </button>
            )}
            {/* 🎪 Presentation Toolbar */}
            {!exportMode && !embedMode && !viewerMode && !authoringMode && <PresentationToolbar
                onLoadPresentation={handleToolbarLoadPresentation}
                onEditPresentation={handleToolbarEditPresentation}
                onShowSettings={handleToolbarShowSettings}
                onShowAuth={handleToolbarShowAuth}
                onTogglePresentation={handleTogglePresentation}
                onCycleLayouter={handleCycleLayouter}
                onTriggerAR={() => appRef.current?.triggerAppleQuickLookAR()}
                onSaveCurrentSettings={handleSaveCurrentSettings}
                onSaveCameraPosition={handleSaveCameraPosition}
                contentPostId={(currentPresentation as any)?.contentPostId}
                onSave={async () => { await handleMarkdownSave(currentMarkdown); }}
                onSaveAs={async () => {
                    const name = prompt('Save As: Enter a new name (without .md)', (currentPresentation?.title || currentFilename.replace(/\.md$/,'')) || 'Untitled');
                    if (!name) return;
                    const newFileName = name.endsWith('.md') ? name : `${name}.md`;
                    const newTitle = name;
                    const updated = updateMarkdownTitle(currentMarkdown, newTitle);
                    setCurrentMarkdown(updated);
                    setCurrentFilename(newFileName);
                    setCurrentPresentation({ source: 'sdk', fileName: newFileName, title: newTitle });
                    try { persistLastOpened(updated, newFileName); } catch {}
                    if (user) {
                        try {
                            const saved = await sdkStorageProvider.saveOrUpdatePresentation(updated, newTitle, false, user.email || null);
                            setCurrentPresentation(prev => prev ? { ...prev, storageId: saved.id, source: 'sdk' as const } : { source: 'sdk' as const, fileName: newFileName, title: newTitle, storageId: saved.id });
                        } catch {}
                    }
                    if (appRef.current) { await appRef.current.loadPresentationFromMarkdown(updated, newFileName); }
                }}
                onReload={async () => {
                    if (!currentPresentation) return;
                    if (currentPresentation.storageId) {
                        try {
                            const full = await sdkStorageProvider.loadPresentationByObjectId(Number(currentPresentation.storageId));
                            if (full?.content) {
                                setCurrentMarkdown(full.content);
                                setCurrentFilename(full.filename);
                                if (appRef.current) { await appRef.current.loadPresentationFromMarkdown(full.content, full.filename); }
                            }
                        } catch (e) {
                            console.warn('Reload from SDK failed:', e);
                            if (appRef.current) { await appRef.current.loadPresentationFromMarkdown(currentMarkdown, currentFilename); }
                        }
                    } else {
                        if (appRef.current) { await appRef.current.loadPresentationFromMarkdown(currentMarkdown, currentFilename); }
                    }
                }}
                currentTitle={(currentPresentation?.storageId ? `#${currentPresentation.storageId} • ` : '') + (currentPresentation?.title || currentFilename)}
                onRenameTitle={(newTitle: string) => {
                    const updated = updateMarkdownTitle(currentMarkdown, newTitle);
                    setCurrentMarkdown(updated);
                    setCurrentPresentation(prev => prev ? { ...prev, title: newTitle } : { source: 'local', fileName: currentFilename, title: newTitle });
                }}
                currentLayouter={availableLayouters[currentLayouterIndex]}
                isPresentationMode={isPresentationMode}
                isAuthenticated={!!user}
                user={user}
                isSaving={isSaving}
                isDirty={isDirty}
                onExport={() => ExportManager.download({
                    width: 1920,
                    height: 1080,
                    format: 'png',
                    filename: `${currentPresentation?.title || 'export'}-${Date.now()}.png`,
                })}
            />}

            {/* 📍 Bottom Navigation for POI Navigation */}
            {/* Vorschau der Autorenoberfläche (?editor=1), bis product/story die echte
                Datenschicht liefert und der Router /edit übernimmt. */}
            {typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('editor') === '1' && (
                <EditorPreview onFocusStep={(_id, i) => { try { (window as any).engineApp?.setActivePOIByIndex?.(i); } catch {} }} />
            )}

            {viewerMode && <ViewerChrome
                title={currentFilename && currentFilename !== 'Untitled.md' ? currentFilename.replace(/\.md$/i, '') : undefined}
                currentPOI={currentPOI}
                totalPOIs={totalPOIs}
                presentationId={presentationId}
            />}

            {!exportMode && !embedMode && !authoringMode && <BottomNavigation
                onPreviousPOI={handleToolbarPreviousPOI}
                onNextPOI={handleToolbarNextPOI}
                onToggleAutoPlay={handleToggleAutoPlay}
                isAutoPlaying={isAutoPlaying}
                isPresentationMode={isPresentationMode}
                currentPOI={currentPOI}
                totalPOIs={totalPOIs}
            />}

            {!hideAllUI && showDebugConsole && <DebugConsole messages={debugMessages} />}
            {!hideAllUI && isTooltipRendered && (
                <Tooltip 
                    visible={tooltip.visible} 
                    content={tooltip.content} 
                    x={tooltip.x} 
                    y={tooltip.y} 
                />
            )}

            {/* --- Global Settings Panel --- */}
            {!hideAllUI && showGlobalSettings && (
            <SettingsPanel
                title="Global Settings"
                properties={globalProperties}
                onPropertyChanged={handlePropertyChanged}
                onClose={() => setShowGlobalSettings(false)}
                initialPosition={{ x: 20, y: 20 }}
            />
            )}

            {/* --- Floating Object-Specific Settings Panels --- */}
            {!hideAllUI && floatingPanels.map((panel, index) => {
                // Check if this is a QuickFinder panel (multiple properties)
                if (panel.id.startsWith('quickfinder-') && panel.quickFinderProperties) {
                    return (
                        <SettingsPanel
                            key={panel.id}
                            title={panel.title}
                            properties={panel.quickFinderProperties}
                            onPropertyChanged={handlePropertyChanged}
                            onClose={() => handleClosePanel(panel.id)}
                            initialPosition={{ x: panel.x, y: panel.y }}
                        />
                    );
                }

                // Regular POI panel
                const poi = appRef.current?.getPOI(panel.id);
                if (!poi) return null;

                const properties = poi.getPropertiesForUI();
                return (
                    <SettingsPanel
                        key={panel.id}
                        title={panel.title}
                        properties={properties}
                        onPropertyChanged={handlePropertyChanged}
                        onClose={() => handleClosePanel(panel.id)}
                        initialPosition={{ x: panel.x, y: panel.y }}
                    />
                );
            })}

            {/* --- Advanced Log Modal --- */}
            {!hideAllUI && (
                <LogModal
                    isVisible={showLogModal}
                    onClose={() => setShowLogModal(false)}
                />
            )}

            {/* --- Parser Test Modal --- */}
            {!hideAllUI && (
                <ParserTestModal
                    isVisible={showParserTest}
                    onClose={() => setShowParserTest(false)}
                />
            )}

            {/* --- Settings Quick Finder --- */}
            {!hideAllUI && (
                <SettingsQuickFinder
                    isVisible={showQuickFinder}
                    onClose={() => setShowQuickFinder(false)}
                    onSearchResultsSelected={handleQuickFinderSearchResults}
                    app={appRef.current}
                />
            )}

            {/* --- Quick Open Overlay (Thumbnail Grid) --- */}
            {!hideAllUI && (
                <QuickOpenOverlay
                    show={showQuickOpen}
                    onClose={() => setShowQuickOpen(false)}
                    onLoadPresentation={handleLoadPresentation}
                    user={user as any}
                    onOpenEditor={(initial, filename) => {
                        setCurrentMarkdown(initial);
                        setCurrentFilename(filename || 'Untitled.md');
                        setShowMarkdownEditor(true);
                    }}
                    onImportTrack={async (trackId, opts) => {
                        await appRef.current?.importTrackFromSdk(trackId, opts);
                    }}
                />
            )}

            {/* --- Authentication Modal --- */}
            {!hideAllUI && (
                <AuthModal
                    show={showAuthModal}
                    onClose={() => setShowAuthModal(false)}
                    onAuthSuccess={handleAuthSuccess}
                />
            )}

            {/* --- User Profile Modal --- */}
            {!hideAllUI && (
                <UserProfileModal
                    show={showUserProfile}
                    onClose={() => setShowUserProfile(false)}
                    user={user}
                    onSignOut={handleSignOut}
                />
            )}

            {/* --- Markdown Editor Modal --- */}
            {!hideAllUI && (
                <MarkdownEditorModal
                    isVisible={showMarkdownEditor}
                    onClose={() => setShowMarkdownEditor(false)}
                    onSave={handleMarkdownSave}
                    initialContent={currentMarkdown}
                    title={`Edit: ${currentFilename}`}
                />
            )}

            {/* 🎪 SHOW LANDING BUTTON (top-left corner) - Only for returning users */}
            {!hideAllUI && showLandingButton && onShowLanding && (
                <button
                    style={{
                        position: 'absolute',
                        top: '16px',
                        left: '16px',
                        zIndex: 1000,
                        padding: '10px 16px',
                        background: 'rgba(0, 0, 0, 0.8)',
                        color: '#ffffff',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                    onClick={onShowLanding}
                    onMouseEnter={(e) => {
                        const target = e.target as HTMLButtonElement;
                        target.style.background = 'rgba(255, 255, 255, 0.1)';
                        target.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        const target = e.target as HTMLButtonElement;
                        target.style.background = 'rgba(0, 0, 0, 0.8)';
                        target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                    }}
                >
                    🎪 Show Landing
                </button>
            )}

            {/* User authentication is now handled via Landing Modal */}

            {/* Firebase Disabled Warning (top-right corner when Firebase disabled).
                Nur im Arbeitsmodus: ein Setup-Hinweis an Entwickler hat vor Publikum
                nichts verloren — hideAllUI deckt Viewer, Embed, Praesentation, Export. */}
            {!firebaseEnabled && !hideAllUI && (
                <div style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    zIndex: 1000
                }}>
                    <div
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#ff9800',
                            color: '#000',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            cursor: 'pointer'
                        }}
                        onClick={() => alert('🔥 Firebase not configured\n\nTo enable cloud features:\n1. Create .env file with Firebase credentials\n2. Restart dev server\n\nSee FIREBASE_SETUP.md for setup instructions.')}
                        title="Click for Firebase setup instructions"
                    >
                        🔥 Cloud Disabled
                    </div>
                </div>
            )}

            {/* Canvas and CSS container for the 3D scene */}
            <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
            <div ref={cssContainerRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />

            {/* Label Diagnostics Overlay (Ctrl/Cmd+Shift+L) */}
            {showLabelDiag && (
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0,0,0,0.55)',
                    zIndex: 2000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }} onClick={() => setShowLabelDiag(false)}>
                    <div style={{
                        width: '80%',
                        height: '70%',
                        background: '#111',
                        color: '#eee',
                        border: '1px solid #444',
                        borderRadius: 12,
                        boxShadow: '0 10px 40px rgba(0,0,0,0.6)',
                        display: 'flex',
                        flexDirection: 'column'
                    }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ padding: 12, borderBottom: '1px solid #333', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ fontWeight: 600 }}>Label Diagnostics</div>
                            <button style={{ background: '#333', color: '#fff', border: '1px solid #555', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }} onClick={() => setShowLabelDiag(false)}>Close</button>
                        </div>
                        <textarea
                            readOnly
                            value={labelDiagText}
                            style={{ flex: 1, background: '#0b0b0b', color: '#e0e0e0', padding: 12, border: 'none', outline: 'none', resize: 'none', fontFamily: 'ui-monospace, Menlo, Monaco, "Courier New", monospace', fontSize: 12 }}
                        />
                        <div style={{ padding: 10, borderTop: '1px solid #333', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button
                                style={{ background: '#1e88e5', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}
                                onClick={() => {
                                    try { navigator.clipboard.writeText(labelDiagText); } catch {}
                                }}
                            >Copy</button>
                            <button
                                style={{ background: '#444', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}
                                onClick={() => collectLabelDiagnostics()}
                            >Refresh</button>
                        </div>
                    </div>
                </div>
            )}
            {/* Mobile Debug Console */}
            {/* MobileDebugConsole entfernt — Remote-Logging läuft zentral via main.jsx,
                der Floating-Lupen-Button war nur eine visuelle Debug-Hilfe. */}
            {/* <MobileDebugConsole /> */}

            {/* Save Toast */}
            {saveToast && (
                <div style={{
                    position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999,
                    padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 500,
                    background: saveToast.type === 'success' ? 'rgba(34,139,34,0.9)' : saveToast.type === 'error' ? 'rgba(220,20,60,0.9)' : 'rgba(30,144,255,0.9)',
                    color: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', backdropFilter: 'blur(8px)'
                }}>{saveToast.message}</div>
            )}
        </div>
    );
};

export default EngineDemoUI;
